import { apiClient } from '@readsavor/api-client';
import { PdfProcessingAttemptStatus } from '@readsavor/types';
/**
 * A service to orchestrate the entire PDF upload and processing workflow.
 * This encapsulates the multi-step complexity from the UI components.
 */
export class PdfUploadService {
    /**
     * Processes a PDF file by uploading it directly to COS and then
     * triggering a server-side processing job.
     *
     * @param file The PDF file to process.
     * @param onUploadProgress A callback to report upload progress (0-100).
     * @returns The final processed data from the PDF.
     */
    async process(file, onUploadProgress) {
        // --- NEW: Frontend validation for file size ---
        const MAX_FILE_SIZE_MB = 20;
        const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
        if (file.size > MAX_FILE_SIZE_BYTES) {
            throw new Error(`File is too large (${(file.size / 1024 / 1024).toFixed(2)} MB). Please upload a file smaller than ${MAX_FILE_SIZE_MB} MB.`);
        }
        let jobId = null;
        try {
            // Step 1: Initiate processing to get a jobId and a presigned URL
            const { jobId: newJobId, uploadUrl } = await apiClient.pdfProcessing.initiateProcessing({
                filename: file.name,
            });
            jobId = newJobId;
            // Step 2: Upload the file directly to the presigned URL
            await this.uploadFile(file, uploadUrl, onUploadProgress);
            // Step 3: Poll for the job to be processed
            await this.pollForStatus(jobId, [PdfProcessingAttemptStatus.PROCESSED]);
            // Step 4: Get the result download URL
            const { downloadUrl } = await apiClient.pdfProcessing.getResultUrl(jobId);
            // Step 5: Download the result and confirm
            try {
                const response = await fetch(downloadUrl);
                if (!response.ok) {
                    throw new Error(`Failed to download result: ${response.statusText}`);
                }
                const result = await response.json();
                // IMPORTANT: Confirm successful download to trigger "read-after-burn"
                await apiClient.pdfProcessing.confirmDownload(jobId);
                return result;
            }
            catch (downloadError) {
                console.error('Download failed.', { jobId, downloadError });
                // Report the failure to the backend
                await apiClient.pdfProcessing.reportDownloadFailure(jobId);
                throw downloadError; // Re-throw to be caught by the outer catch block
            }
        }
        catch (error) {
            console.error('PDF processing failed at some stage.', { error, jobId });
            // If the process fails after a job ID was created, attempt to cancel it.
            // This is a "fire and forget" cleanup attempt.
            if (jobId) {
                apiClient.pdfProcessing.cancelJob(jobId).catch(cancelError => {
                    console.error(`Failed to cancel job ${jobId}. Manual cleanup may be required.`, cancelError);
                });
            }
            // Re-throw the original error to be handled by the caller
            throw error;
        }
    }
    /**
     * Uploads a file to a given URL using a PUT request.
     * Supports progress reporting via XMLHttpRequest.
     */
    uploadFile(file, url, onProgress) {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', url, true);
            // Report progress
            if (onProgress) {
                xhr.upload.onprogress = event => {
                    if (event.lengthComputable) {
                        const progress = Math.round((event.loaded / event.total) * 100);
                        onProgress(progress);
                    }
                };
            }
            // Handle completion
            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    // Ensure progress reports 100% on completion
                    onProgress?.(100);
                    resolve();
                }
                else {
                    reject(new Error(`File upload failed with status: ${xhr.status} ${xhr.statusText}`));
                }
            };
            // Handle errors
            xhr.onerror = () => {
                reject(new Error('File upload failed due to a network error.'));
            };
            xhr.send(file);
        });
    }
    /**
     * Polls the job status endpoint until the job reaches a target status.
     */
    pollForStatus(jobId, targetStatus, interval = 3000, // 3 seconds
    timeout = 300000 // 5 minutes
    ) {
        return new Promise((resolve, reject) => {
            const startTime = Date.now();
            const executePoll = async () => {
                if (Date.now() - startTime > timeout) {
                    reject(new Error(`Polling timed out for job ${jobId} after ${timeout / 1000} seconds.`));
                    return;
                }
                try {
                    const { status } = await apiClient.pdfProcessing.getStatus(jobId);
                    if (targetStatus.includes(status)) {
                        resolve();
                    }
                    else if (status === PdfProcessingAttemptStatus.FAILED ||
                        status === PdfProcessingAttemptStatus.REFUND_FAILED) {
                        reject(new Error(`Job ${jobId} failed with status: ${status}`));
                    }
                    else {
                        // If not the target status or failed, poll again after the interval
                        setTimeout(executePoll, interval);
                    }
                }
                catch (error) {
                    reject(error);
                }
            };
            executePoll();
        });
    }
}
// Export a singleton instance for easy use across the application
export const pdfUploadService = new PdfUploadService();
//# sourceMappingURL=PdfUploadService.js.map